// TEAM OS Hub — i18n (apps.*)
//
// Fully localised in all 15 active+pilot languages (EN, ES, ZH, IT, FR,
// DE, PT-BR, JA, HI, KO, RU, CS, NL, AR, HE). Single source of truth:
// every key below is resolved through the same `pick({ ... }, lang)`
// helper used across the rest of the codebase, so adding a 16th language
// is a one-line change per entry.
//
// AR + HE are kept in `isActive: false` at the dropdown level today,
// but their strings are shipped so the moment the flag flips on
// `languages.js` the hub renders correctly. RTL is already handled
// at the document-level (`<html dir>`) by the existing i18n provider.

const LANGS = [
  "en", "es", "zh", "it", "fr", "de", "pt-br", "ja", "hi", "ko",
  "ru", "cs", "nl", "ar", "he", "ca", "eu", "gl",
];

// Same helper convention as wishlistI18n / calendarI18n: pass a map keyed
// by lang and get the active translation back. Falls back to EN if a
// language slot is missing (defensive — should not happen here since
// every key below provides all 15 languages).
const pick = (m, lang) => m[lang] || m.en;

// Per-module copy. Bullets travel as `\n`-joined strings so we can use
// the existing string-only `t()` API. Order is the SAME across all
// languages — only the wording is translated.
const QUEST_BULLETS = {
  en:    "Create your profile\nComplete questionnaires\nAccess your dashboard\nManage your calendar\nWishlists\nMarketplace requests\nRewards",
  es:    "Crea tu perfil\nCompleta cuestionarios\nAccede a tu panel\nGestiona tu calendario\nWishlists\nSolicitudes de marketplace\nRecompensas",
  zh:    "创建您的个人资料\n完成问卷\n进入您的面板\n管理您的日历\n愿望清单\n市场请求\n奖励",
  it:    "Crea il tuo profilo\nCompila i questionari\nAccedi al tuo pannello\nGestisci il tuo calendario\nWishlist\nRichieste marketplace\nRicompense",
  fr:    "Crée ton profil\nRéponds aux questionnaires\nAccède à ton tableau de bord\nGère ton calendrier\nWishlists\nDemandes marketplace\nRécompenses",
  de:    "Profil erstellen\nFragebögen ausfüllen\nDashboard öffnen\nKalender verwalten\nWunschlisten\nMarktplatz-Anfragen\nBelohnungen",
  "pt-br": "Crie seu perfil\nComplete os questionários\nAcesse seu painel\nGerencie sua agenda\nListas de desejos\nSolicitações de marketplace\nRecompensas",
  ja:    "プロフィールを作成\nアンケートに回答\nダッシュボードにアクセス\nカレンダーを管理\nウィッシュリスト\nマーケットプレイスのリクエスト\nリワード",
  hi:    "अपनी प्रोफ़ाइल बनाएँ\nप्रश्नावली पूरी करें\nअपना डैशबोर्ड देखें\nअपना कैलेंडर प्रबंधित करें\nविशलिस्ट\nमार्केटप्लेस अनुरोध\nपुरस्कार",
  ko:    "프로필 만들기\n설문 완료하기\n대시보드 보기\n캘린더 관리\n위시리스트\n마켓플레이스 요청\n리워드",
  ru:    "Создайте профиль\nЗаполните анкеты\nОткройте панель\nУправляйте календарём\nСписки желаний\nЗапросы маркетплейса\nНаграды",
  cs:    "Vytvořte si profil\nVyplňte dotazníky\nVstupte do dashboardu\nSpravujte svůj kalendář\nSeznamy přání\nPožadavky marketplace\nOdměny",
  nl:    "Maak je profiel\nVul vragenlijsten in\nOpen je dashboard\nBeheer je kalender\nVerlanglijsten\nMarketplace-aanvragen\nBeloningen",
  ar:    "أنشئ ملفك الشخصي\nأكمل الاستبيانات\nادخل إلى لوحتك\nإدارة التقويم\nقوائم الرغبات\nطلبات السوق\nالمكافآت",
  he:    "צור פרופיל\nמלא שאלונים\nגישה ללוח הבקרה\nניהול היומן\nרשימות משאלות\nבקשות שוק\nתגמולים",
  ca: "Crea el teu perfil\nOmple qüestionaris\nAccedeix al teu tauler\nGestiona el teu calendari\nLlistes de desitjos\nSol·licituds del mercat\nRecompenses",
  eu: "Sortu zure profila\nBete galdetegiak\nSartu zure aginte-panelera\nKudeatu zure egutegia\nGustuko-zerrendak\nMerkataritzako eskaerak\nSariak",
  gl: "Crea o teu perfil\nCompleta cuestionarios\nAccede ao teu panel\nXestiona o teu calendario\nListas de desexos\nSolicitudes de mercado\nRecompensas",
};

const RACKET_LEAD = {
  en:    "Manage your rackets:",
  es:    "Gestiona tus raquetas:",
  zh:    "管理您的球拍:",
  it:    "Gestisci le tue racchette:",
  fr:    "Gérez vos raquettes :",
  de:    "Verwalte deine Schläger:",
  "pt-br": "Gerencie suas raquetes:",
  ja:    "ラケットを管理:",
  hi:    "अपने रैकेट प्रबंधित करें:",
  ko:    "당신의 라켓 관리:",
  ru:    "Управляйте ракетками:",
  cs:    "Spravujte své rakety:",
  nl:    "Beheer je rackets:",
  ar:    "إدارة المضارب:",
  he:    "ניהול המחבטים שלך:",
  ca: "Gestiona les teves raquetes:",
  eu: "Kudeatu zure erraketa:",
  gl: "Xestiona as túas raquetas:",
};

const RACKET_BULLETS = {
  en:    "Stringing jobs\nStrings\nEquipment\nOnline booking for stringing service / drop-off / pick-up\nQR check-in\nCommunication with technicians\nTechnical history\nTraining courses\nEquipment care tips\nOffers\nNew products\nSocial media from the pros",
  es:    "Encordados\nCordaje\nMaterial\nFechas de reserva online de servicio de encordado / entrega / recogida\nCheck-in QR\nComunicación con los técnicos\nHistorial técnico\nCursos de formación\nConsejos en cuidado de tu material\nOfertas\nNuevos productos\nRedes sociales de los profesionales",
  zh:    "穿线服务\n网线\n器材\n在线预约穿线 / 交付 / 取回\n二维码签到\n与技师沟通\n技术历史\n培训课程\n器材保养建议\n优惠活动\n新品\n专业人士的社交媒体",
  it:    "Incordature\nCorde\nAttrezzatura\nPrenotazione online del servizio di incordatura / consegna / ritiro\nCheck-in QR\nComunicazione con i tecnici\nStorico tecnico\nCorsi di formazione\nConsigli per la cura della tua attrezzatura\nOfferte\nNuovi prodotti\nSocial dei professionisti",
  fr:    "Cordages effectués\nCordes\nÉquipement\nRéservation en ligne du service de cordage / dépôt / retrait\nCheck-in QR\nCommunication avec les techniciens\nHistorique technique\nFormations\nConseils d'entretien de ton matériel\nPromos\nNouveautés\nRéseaux sociaux des pros",
  de:    "Besaitungsaufträge\nSaiten\nAusrüstung\nOnline-Buchung für Besaitungsservice / Abgabe / Abholung\nQR-Check-in\nKommunikation mit Technikern\nTechnische Historie\nSchulungskurse\nPflegetipps für deine Ausrüstung\nAngebote\nNeue Produkte\nSocial Media der Profis",
  "pt-br": "Cordagens\nCordas\nMaterial\nReserva online de cordagem / entrega / retirada\nCheck-in QR\nComunicação com técnicos\nHistórico técnico\nCursos de formação\nDicas de cuidado do material\nOfertas\nNovidades\nRedes sociais dos profissionais",
  ja:    "ストリンギング作業\nストリング\n用具\nストリンギング/受け渡し/受取のオンライン予約\nQRチェックイン\n技術者とのやり取り\n技術履歴\nトレーニングコース\n用具のお手入れアドバイス\nオファー\n新製品\nプロのSNS",
  hi:    "स्ट्रिंगिंग कार्य\nस्ट्रिंग\nउपकरण\nस्ट्रिंगिंग सेवा / ड्रॉप-ऑफ / पिकअप की ऑनलाइन बुकिंग\nQR चेक-इन\nतकनीशियनों से संवाद\nतकनीकी इतिहास\nप्रशिक्षण पाठ्यक्रम\nउपकरण देखभाल सुझाव\nऑफ़र\nनए उत्पाद\nपेशेवरों के सोशल मीडिया",
  ko:    "스트링 작업\n스트링\n장비\n스트링 서비스 / 접수 / 수령 온라인 예약\nQR 체크인\n기술자와의 소통\n기술 이력\n교육 과정\n장비 관리 팁\n프로모션\n신제품\n프로 선수의 SNS",
  ru:    "Натяжка струн\nСтруны\nЭкипировка\nОнлайн-запись на натяжку / сдачу / получение\nQR-чек-ин\nСвязь с техниками\nТехническая история\nОбучающие курсы\nСоветы по уходу за инвентарём\nАкции\nНовинки\nСоцсети профессионалов",
  cs:    "Vyplétání\nStruny\nVybavení\nOnline rezervace vyplétání / odevzdání / vyzvednutí\nQR check-in\nKomunikace s techniky\nTechnická historie\nŠkolicí kurzy\nTipy na péči o vybavení\nAkce\nNové produkty\nSociální sítě profesionálů",
  nl:    "Bespanningen\nSnaren\nUitrusting\nOnline boeking voor bespanning / inleveren / ophalen\nQR-check-in\nCommunicatie met technici\nTechnische historie\nTrainingscursussen\nOnderhoudstips voor je materiaal\nAanbiedingen\nNieuwe producten\nSocial media van de pros",
  ar:    "أعمال التوتير\nالأوتار\nالمعدات\nالحجز الإلكتروني لخدمة التوتير / التسليم / الاستلام\nتسجيل الدخول QR\nالتواصل مع الفنيين\nالسجل الفني\nدورات تدريبية\nنصائح للعناية بمعداتك\nالعروض\nمنتجات جديدة\nوسائل التواصل للمحترفين",
  he:    "מתיחות מיתרים\nמיתרים\nציוד\nהזמנה אונליין לשירות מיתוח / מסירה / איסוף\nצ׳ק-אין QR\nתקשורת עם הטכנאים\nהיסטוריה טכנית\nקורסי הכשרה\nטיפים לטיפול בציוד\nמבצעים\nמוצרים חדשים\nרשתות חברתיות של המקצוענים",
  ca: "Encordats\nCordes\nEquipament\nReserva en línia per al servei d'encordat / lliurament / recollida\nRegistre amb QR\nComunicació amb tècnics\nHistorial tècnic\nCursos de formació\nConsells de manteniment de l'equipament\nOfertes\nProductes nous\nXarxes socials dels professionals",
  eu: "Harikadura-lanak\nHariak\nEkipamendua\nLineako erreserba harikadura zerbitzurako / uzteko / jasotzeko\nQR sarrera\nTeknikariekin komunikazioa\nHistorial teknikoa\nPrestakuntza ikastaroak\nEkipamendua zaintzeko aholkuak\nEskaintzak\nProduKtu berriak\nProfesionalen sare sozialak",
  gl: "Traballos de encordado\nCordas\nEquipamento\nReserva en liña para servizo de encordado / entrega / recollida\nRexistro QR\nComunicación con técnicos\nHistorial técnico\nCursos de formación\nConsellos de coidado do equipamento\nOfertas\nNovos produtos\nRedes sociais dos profesionais",
};

const buildApps = (lang) => ({
  heroBadge: "TEAM OS Hub",
  heroTitle: pick({
    en: "One TEAM account.\nMany tools.",
    es: "Una cuenta TEAM.\nMuchas herramientas.",
    zh: "一个 TEAM 账户。\n众多工具。",
    it: "Un account TEAM.\nMolti strumenti.",
    fr: "Un compte TEAM.\nDe nombreux outils.",
    de: "Ein TEAM-Konto.\nViele Tools.",
    "pt-br": "Uma conta TEAM.\nMuitas ferramentas.",
    ja: "ひとつの TEAM アカウント。\nたくさんのツール。",
    hi: "एक TEAM खाता.\nकई उपकरण.",
    ko: "하나의 TEAM 계정.\n다양한 도구.",
    ru: "Один аккаунт TEAM.\nМножество инструментов.",
    cs: "Jeden účet TEAM.\nMnoho nástrojů.",
    nl: "Eén TEAM-account.\nVeel tools.",
    ar: "حساب TEAM واحد.\nأدوات عديدة.",
    he: "חשבון TEAM אחד.\nכלים רבים.",
    ca: "Un compte TEAM.\nMoltes eines.",
    eu: "TEAM kontu bakarra.\nTresna ugari.",
    gl: "Unha conta TEAM.\nMoitas ferramentas.",
  }, lang),
  heroSubtitle: pick({
    en: "Activate the modules you need, from market validation to racket lab, nutrition, training, travel, health and sponsorships. All connected through your TEAM profile.",
    es: "Activa los módulos que necesitas, desde validación de mercado hasta laboratorio de raquetas, nutrición, entrenamiento, viajes, salud y patrocinios. Todos conectados a tu perfil TEAM.",
    zh: "激活您所需的模块,从市场验证到球拍实验室、营养、训练、旅行、健康和赞助。所有模块都与您的 TEAM 资料相连。",
    it: "Attiva i moduli che ti servono, dalla validazione di mercato al laboratorio racchette, nutrizione, allenamento, viaggi, salute e sponsorizzazioni. Tutti collegati al tuo profilo TEAM.",
    fr: "Active les modules dont tu as besoin, de la validation marché au labo raquettes, nutrition, entraînement, voyages, santé et sponsoring. Tous reliés à ton profil TEAM.",
    de: "Aktiviere die Module, die du brauchst – von der Marktvalidierung bis zum Racket Lab, Ernährung, Training, Reisen, Gesundheit und Sponsoring. Alle mit deinem TEAM-Profil verbunden.",
    "pt-br": "Ative os módulos que precisar, da validação de mercado ao laboratório de raquetes, nutrição, treino, viagens, saúde e patrocínios. Todos conectados ao seu perfil TEAM.",
    ja: "市場検証からラケット ラボ、栄養、トレーニング、旅行、健康、スポンサーシップまで、必要なモジュールを有効化。すべて TEAM プロフィールでつながります。",
    hi: "अपनी ज़रूरत के मॉड्यूल सक्रिय करें — मार्केट वैलिडेशन से लेकर रैकेट लैब, पोषण, प्रशिक्षण, यात्रा, स्वास्थ्य और प्रायोजन तक. सभी आपके TEAM प्रोफ़ाइल से जुड़े.",
    ko: "시장 검증부터 라켓 랩, 영양, 트레이닝, 여행, 건강, 스폰서십까지 필요한 모듈을 활성화하세요. 모두 당신의 TEAM 프로필과 연결됩니다.",
    ru: "Активируйте нужные модули — от валидации рынка до лаборатории ракеток, питания, тренировок, путешествий, здоровья и спонсорства. Всё связано с вашим профилем TEAM.",
    cs: "Aktivujte moduly, které potřebujete — od validace trhu po Racket Lab, výživu, trénink, cestování, zdraví a sponzoring. Vše propojené s vaším TEAM profilem.",
    nl: "Activeer de modules die je nodig hebt — van marktvalidatie tot racketlab, voeding, training, reizen, gezondheid en sponsoring. Allemaal verbonden via je TEAM-profiel.",
    ar: "فعّل الوحدات التي تحتاجها، من التحقق من السوق إلى مختبر المضارب والتغذية والتدريب والسفر والصحة والرعاية. جميعها متصلة بملفك في TEAM.",
    he: "הפעל את המודולים שאתה צריך — מאימות שוק ועד מעבדת מחבטים, תזונה, אימון, נסיעות, בריאות וחסויות. הכל מחובר לפרופיל TEAM שלך.",
    ca: "Activa els mòduls que necessitis, des de validació de mercat fins a laboratori de raquetes, nutrició, entrenament, viatges, salut i patrocinis. Tot connectat a través del teu perfil TEAM.",
    eu: "Aktibatu behar dituzun moduluak, merkatuaren balioztapenetik erraketa laborategira, nutrizioa, entrenamendu, bidaia, osasuna eta babesleak. Guztiak zure TEAM profilaren bidez konektatuta.",
    gl: "Activa os módulos que necesites, desde validación de mercado ata laboratorio de raquetas, nutrición, adestramento, viaxes, saúde e patrocinios. Todo conectado a través do teu perfil TEAM.",
  }, lang),
  heroPrinciple: pick({
    en: "Register once.\nActivate only what you need.\nYour data, your rules.",
    es: "Regístrate una vez.\nActiva sólo lo que necesitas.\nTus datos, tus reglas.",
    zh: "注册一次。\n只激活您需要的内容。\n您的数据,您做主。",
    it: "Registrati una volta.\nAttiva solo ciò che ti serve.\nI tuoi dati, le tue regole.",
    fr: "Inscris-toi une fois.\nN'active que ce dont tu as besoin.\nTes données, tes règles.",
    de: "Einmal registrieren.\nNur aktivieren, was du brauchst.\nDeine Daten, deine Regeln.",
    "pt-br": "Cadastre-se uma vez.\nAtive somente o que você precisa.\nSeus dados, suas regras.",
    ja: "登録は一度きり。\n必要なものだけ有効化。\nあなたのデータ、あなたのルール。",
    hi: "एक बार पंजीकरण करें.\nजो ज़रूरी है केवल वही सक्रिय करें.\nआपका डेटा, आपके नियम.",
    ko: "한 번 등록.\n필요한 것만 활성화.\n당신의 데이터, 당신의 규칙.",
    ru: "Регистрируйтесь один раз.\nАктивируйте только то, что нужно.\nВаши данные, ваши правила.",
    cs: "Zaregistrujte se jednou.\nAktivujte jen to, co potřebujete.\nVaše data, vaše pravidla.",
    nl: "Eén keer registreren.\nAlleen activeren wat je nodig hebt.\nJouw data, jouw regels.",
    ar: "سجل مرة واحدة.\nفعّل ما تحتاجه فقط.\nبياناتك، قواعدك.",
    he: "הירשם פעם אחת.\nהפעל רק את מה שצריך.\nהנתונים שלך, החוקים שלך.",
    ca: "Registra't una vegada.\nActiva només el que necessitis.\nLes teves dades, les teves regles.",
    eu: "Erregistratu behin.\nAktibatu behar duzuna bakarrik.\nZure datuak, zure arauak.",
    gl: "Rexístrate unha vez.\nActiva só o que necesites.\nOs teus datos, as túas regras.",
  }, lang),
  statusActive: pick({
    en: "Active", es: "Activa", zh: "已启用", it: "Attivo", fr: "Actif", de: "Aktiv",
    "pt-br": "Ativo", ja: "有効", hi: "सक्रिय", ko: "활성", ru: "Активно",
    cs: "Aktivní", nl: "Actief", ar: "نشط", he: "פעיל",
    ca: "Actiu",
    eu: "Aktiboa",
    gl: "Activo",
  }, lang),
  statusComingSoon: pick({
    en: "Coming soon", es: "Próximamente", zh: "即将推出", it: "In arrivo", fr: "Bientôt",
    de: "Demnächst", "pt-br": "Em breve", ja: "近日公開", hi: "जल्द आ रहा है",
    ko: "곧 출시", ru: "Скоро", cs: "Již brzy", nl: "Binnenkort", ar: "قريبًا", he: "בקרוב",
    ca: "Properament",
    eu: "Laster",
    gl: "Próximamente",
  }, lang),
  openApp: pick({
    en: "Open app", es: "Abrir app", zh: "打开应用", it: "Apri app", fr: "Ouvrir l'app",
    de: "App öffnen", "pt-br": "Abrir app", ja: "アプリを開く", hi: "ऐप खोलें",
    ko: "앱 열기", ru: "Открыть приложение", cs: "Otevřít aplikaci", nl: "App openen",
    ar: "فتح التطبيق", he: "פתח את האפליקציה",
    ca: "Obre l'aplicació",
    eu: "Ireki aplikazioa",
    gl: "Abrir aplicación",
  }, lang),
  learnMore: pick({
    en: "Learn more", es: "Saber más", zh: "了解更多", it: "Scopri di più", fr: "En savoir plus",
    de: "Mehr erfahren", "pt-br": "Saiba mais", ja: "詳細を見る", hi: "और जानें",
    ko: "자세히 보기", ru: "Подробнее", cs: "Více informací", nl: "Meer info",
    ar: "اعرف المزيد", he: "מידע נוסף",
    ca: "Més informació",
    eu: "Ikasi gehiago",
    gl: "Máis información",
  }, lang),
  backToHub: pick({
    en: "Back to TEAM OS Hub", es: "Volver al TEAM OS Hub", zh: "返回 TEAM OS Hub",
    it: "Torna a TEAM OS Hub", fr: "Retour au TEAM OS Hub", de: "Zurück zum TEAM OS Hub",
    "pt-br": "Voltar ao TEAM OS Hub", ja: "TEAM OS Hub に戻る", hi: "TEAM OS Hub पर वापस जाएँ",
    ko: "TEAM OS Hub로 돌아가기", ru: "Назад в TEAM OS Hub", cs: "Zpět na TEAM OS Hub",
    nl: "Terug naar TEAM OS Hub", ar: "العودة إلى TEAM OS Hub", he: "חזרה ל-TEAM OS Hub",
    ca: "Torna a TEAM OS Hub",
    eu: "Itzuli TEAM OS Hub-era",
    gl: "Volver a TEAM OS Hub",
  }, lang),
  goToActive: pick({
    en: "Open TEAM Questionnaires", es: "Abrir TEAM Questionnaires",
    zh: "打开 TEAM Questionnaires", it: "Apri TEAM Questionnaires",
    fr: "Ouvrir TEAM Questionnaires", de: "TEAM Questionnaires öffnen",
    "pt-br": "Abrir TEAM Questionnaires", ja: "TEAM Questionnaires を開く",
    hi: "TEAM Questionnaires खोलें", ko: "TEAM Questionnaires 열기",
    ru: "Открыть TEAM Questionnaires", cs: "Otevřít TEAM Questionnaires",
    nl: "TEAM Questionnaires openen", ar: "افتح TEAM Questionnaires",
    he: "פתח את TEAM Questionnaires",
    ca: "Obre TEAM Questionnaires",
    eu: "Ireki TEAM Galdetegiak",
    gl: "Abrir TEAM Questionnaires",
  }, lang),
  willSolve: pick({
    en: "What it will solve", es: "Qué resolverá", zh: "将解决什么", it: "Cosa risolverà",
    fr: "Ce qu'il résoudra", de: "Was es löst", "pt-br": "O que resolverá",
    ja: "解決すること", hi: "यह क्या हल करेगा", ko: "해결할 문제",
    ru: "Что решает", cs: "Co to vyřeší", nl: "Wat het oplost",
    ar: "ما الذي سيحله", he: "מה זה יפתור",
    ca: "Què resoldrà",
    eu: "Zer konponduko duen",
    gl: "O que resolverá",
  }, lang),
  coreReused: pick({
    en: "Core AI Capabilities", es: "Capacidades Core AI",
    zh: "核心 AI 能力", it: "Capacità Core AI",
    fr: "Capacités Core AI", de: "Core-AI-Fähigkeiten",
    "pt-br": "Capacidades Core AI", ja: "Core AI 機能",
    hi: "कोर AI क्षमताएँ", ko: "코어 AI 기능",
    ru: "Возможности Core AI", cs: "Core AI schopnosti",
    nl: "Core AI mogelijkheden", ar: "قدرات Core AI",
    he: "יכולות Core AI",
    ca: "Capacitats principals d'IA",
    eu: "AI Gaitasun Nagusiak",
    gl: "Capacidades básicas de IA",
  }, lang),
  // Feb 2026 — Core AI capability labels, surfaced in a 3-col grid on
  // the coming-soon sublanding. Keys MUST match the snake_case tokens
  // declared in `modules/registry.js` → `core: [...]`. Order in the UI
  // comes from the registry, not from this map.
  coreCaps: {
    team_universe: pick({
      en: "TEAM Universe", es: "TEAM Universe", zh: "TEAM Universe", it: "TEAM Universe",
      fr: "TEAM Universe", de: "TEAM Universe", "pt-br": "TEAM Universe", ja: "TEAM Universe",
      hi: "TEAM Universe", ko: "TEAM Universe", ru: "TEAM Universe", cs: "TEAM Universe",
      nl: "TEAM Universe", ar: "TEAM Universe", he: "TEAM Universe",
      ca: "TEAM Universe",
      eu: "TEAM Unibertsoa",
      gl: "TEAM Universe",
    }, lang),
    intelligent_management: pick({
      en: "Intelligent Management", es: "Gestión Inteligente", zh: "智能管理",
      it: "Gestione Intelligente", fr: "Gestion Intelligente", de: "Intelligentes Management",
      "pt-br": "Gestão Inteligente", ja: "インテリジェント管理",
      hi: "बुद्धिमान प्रबंधन", ko: "지능형 관리",
      ru: "Интеллектуальное управление", cs: "Inteligentní správa",
      nl: "Intelligent Beheer", ar: "إدارة ذكية", he: "ניהול חכם",
      ca: "Gestió intel·ligent",
      eu: "Kudeaketa Adimentsua",
      gl: "Xestión intelixente",
    }, lang),
    marketplace: pick({
      en: "Marketplace", es: "Marketplace", zh: "Marketplace", it: "Marketplace",
      fr: "Marketplace", de: "Marketplace", "pt-br": "Marketplace", ja: "Marketplace",
      hi: "Marketplace", ko: "Marketplace", ru: "Marketplace", cs: "Marketplace",
      nl: "Marketplace", ar: "Marketplace", he: "Marketplace",
      ca: "Mercat",
      eu: "Merkatua",
      gl: "Mercado",
    }, lang),
    profiles: pick({
      en: "Profiles", es: "Perfiles", zh: "个人资料", it: "Profili",
      fr: "Profils", de: "Profile", "pt-br": "Perfis", ja: "プロフィール",
      hi: "प्रोफ़ाइल", ko: "프로필", ru: "Профили", cs: "Profily",
      nl: "Profielen", ar: "الملفات الشخصية", he: "פרופילים",
      ca: "Perfils",
      eu: "Profilak",
      gl: "Perfís",
    }, lang),
    wishlist: pick({
      en: "Wishlist", es: "Wishlist", zh: "心愿单", it: "Wishlist",
      fr: "Wishlist", de: "Wishlist", "pt-br": "Wishlist", ja: "ウィッシュリスト",
      hi: "विशलिस्ट", ko: "위시리스트", ru: "Список желаний", cs: "Wishlist",
      nl: "Wishlist", ar: "قائمة الرغبات", he: "רשימת משאלות",
      ca: "Llista de desitjos",
      eu: "Gustuko-zerrenda",
      gl: "Lista de desexos",
    }, lang),
    calendar: pick({
      en: "Calendar", es: "Calendario", zh: "日历", it: "Calendario",
      fr: "Calendrier", de: "Kalender", "pt-br": "Calendário", ja: "カレンダー",
      hi: "कैलेंडर", ko: "캘린더", ru: "Календарь", cs: "Kalendář",
      nl: "Kalender", ar: "التقويم", he: "יומן",
      ca: "Calendari",
      eu: "Egutegia",
      gl: "Calendario",
    }, lang),
    notifications: pick({
      en: "Notifications", es: "Notificaciones", zh: "通知", it: "Notifiche",
      fr: "Notifications", de: "Benachrichtigungen", "pt-br": "Notificações", ja: "通知",
      hi: "सूचनाएँ", ko: "알림", ru: "Уведомления", cs: "Oznámení",
      nl: "Notificaties", ar: "الإشعارات", he: "התראות",
      ca: "Notificacions",
      eu: "Jakinarazpenak",
      gl: "Notificacións",
    }, lang),
    settings: pick({
      en: "Settings", es: "Ajustes", zh: "设置", it: "Impostazioni",
      fr: "Paramètres", de: "Einstellungen", "pt-br": "Configurações", ja: "設定",
      hi: "सेटिंग्स", ko: "설정", ru: "Настройки", cs: "Nastavení",
      nl: "Instellingen", ar: "الإعدادات", he: "הגדרות",
      ca: "Configuració",
      eu: "Ezarpenak",
      gl: "Configuración",
    }, lang),
    community_of_users: pick({
      en: "Community of Users", es: "Comunidad de Usuarios", zh: "用户社区",
      it: "Comunità di Utenti", fr: "Communauté d'Utilisateurs",
      de: "Nutzer-Community", "pt-br": "Comunidade de Usuários",
      ja: "ユーザーコミュニティ", hi: "उपयोगकर्ताओं का समुदाय",
      ko: "사용자 커뮤니티", ru: "Сообщество пользователей",
      cs: "Komunita uživatelů", nl: "Gebruikerscommunity",
      ar: "مجتمع المستخدمين", he: "קהילת משתמשים",
      ca: "Comunitat d'usuaris",
      eu: "Erabiltzaileen Komunitatea",
      gl: "Comunidade de usuarios",
    }, lang),
    consent: pick({
      en: "Consent", es: "Consentimiento", zh: "同意", it: "Consenso",
      fr: "Consentement", de: "Einwilligung", "pt-br": "Consentimento", ja: "同意",
      hi: "सहमति", ko: "동의", ru: "Согласие", cs: "Souhlas",
      nl: "Toestemming", ar: "الموافقة", he: "הסכמה",
      ca: "Consentiment",
      eu: "Baimena",
      gl: "Consentimento",
    }, lang),
    qr: pick({
      en: "QR", es: "QR", zh: "QR", it: "QR", fr: "QR", de: "QR",
      "pt-br": "QR", ja: "QR", hi: "QR", ko: "QR", ru: "QR", cs: "QR",
      nl: "QR", ar: "QR", he: "QR",
      ca: "QR",
      eu: "QR",
      gl: "QR",
    }, lang),
    auth: pick({
      en: "Auth", es: "Autenticación", zh: "身份验证", it: "Autenticazione",
      fr: "Authentification", de: "Authentifizierung", "pt-br": "Autenticação",
      ja: "認証", hi: "प्रमाणीकरण", ko: "인증",
      ru: "Аутентификация", cs: "Ověření", nl: "Authenticatie",
      ar: "المصادقة", he: "אימות",
      ca: "Autenticació",
      eu: "Autentifikazioa",
      gl: "Autenticación",
    }, lang),
  },
  footerNote: pick({
    en: "TEAM OS Hub v1 · Each module activates only after you opt-in. Sensitive data is never shared between modules without explicit consent.",
    es: "TEAM OS Hub v1 · Cada módulo se activa solo tras tu consentimiento. Los datos sensibles nunca se comparten entre módulos sin consentimiento explícito.",
    zh: "TEAM OS Hub v1 · 每个模块仅在您同意后才会启用。未经明确同意,敏感数据绝不会在模块之间共享。",
    it: "TEAM OS Hub v1 · Ogni modulo si attiva solo dopo il tuo opt-in. I dati sensibili non vengono mai condivisi tra moduli senza consenso esplicito.",
    fr: "TEAM OS Hub v1 · Chaque module ne s'active qu'après ton opt-in. Les données sensibles ne sont jamais partagées entre modules sans consentement explicite.",
    de: "TEAM OS Hub v1 · Jedes Modul wird erst nach deinem Opt-in aktiviert. Sensible Daten werden ohne ausdrückliche Zustimmung nie zwischen Modulen geteilt.",
    "pt-br": "TEAM OS Hub v1 · Cada módulo é ativado somente após o seu opt-in. Dados sensíveis nunca são compartilhados entre módulos sem consentimento explícito.",
    ja: "TEAM OS Hub v1 · 各モジュールはオプトイン後にのみ有効化されます。機密データは明示的な同意なしにモジュール間で共有されることはありません。",
    hi: "TEAM OS Hub v1 · प्रत्येक मॉड्यूल आपकी सहमति के बाद ही सक्रिय होता है. संवेदनशील डेटा बिना स्पष्ट सहमति के मॉड्यूल के बीच कभी साझा नहीं किया जाता.",
    ko: "TEAM OS Hub v1 · 각 모듈은 옵트인 후에만 활성화됩니다. 명시적 동의 없이는 민감한 데이터가 모듈 간에 공유되지 않습니다.",
    ru: "TEAM OS Hub v1 · Каждый модуль активируется только после вашего согласия. Чувствительные данные никогда не передаются между модулями без явного согласия.",
    cs: "TEAM OS Hub v1 · Každý modul se aktivuje až po vašem souhlasu. Citlivá data se mezi moduly nikdy nesdílí bez výslovného souhlasu.",
    nl: "TEAM OS Hub v1 · Elke module wordt pas geactiveerd na jouw opt-in. Gevoelige data wordt zonder uitdrukkelijke toestemming nooit gedeeld tussen modules.",
    ar: "TEAM OS Hub v1 · يتم تفعيل كل وحدة فقط بعد موافقتك. لا تتم مشاركة البيانات الحساسة بين الوحدات دون موافقة صريحة.",
    he: "TEAM OS Hub v1 · כל מודול מופעל רק לאחר הסכמתך. מידע רגיש אינו משותף בין מודולים ללא הסכמה מפורשת.",
    ca: "TEAM OS Hub v1 · Cada mòdul s'activa només després que hi donis el consentiment. Les dades sensibles no es comparteixen mai entre mòduls sense consentiment explícit.",
    eu: "TEAM OS Hub v1 · Modulu bakoitza onarpena eman ondoren bakarrik aktibatzen da. Datu sentikorrak ez dira inoiz moduluen artean partekatzen baimen espliziturik gabe.",
    gl: "TEAM OS Hub v1 · Cada módulo actívase só despois de que aceptes. Os datos sensibles nunca se comparten entre módulos sen consentimento explícito.",
  }, lang),
  notFoundTitle: pick({
    en: "Module not found", es: "Módulo no encontrado", zh: "找不到模块",
    it: "Modulo non trovato", fr: "Module introuvable", de: "Modul nicht gefunden",
    "pt-br": "Módulo não encontrado", ja: "モジュールが見つかりません",
    hi: "मॉड्यूल नहीं मिला", ko: "모듈을 찾을 수 없음",
    ru: "Модуль не найден", cs: "Modul nenalezen", nl: "Module niet gevonden",
    ar: "الوحدة غير موجودة", he: "המודול לא נמצא",
    ca: "Mòdul no trobat",
    eu: "Modulua ez da aurkitu",
    gl: "Módulo non atopado",
  }, lang),
  notFoundBody: pick({
    en: "This module is not part of the current registry.",
    es: "Este módulo no forma parte del registro actual.",
    zh: "此模块不在当前注册表中。",
    it: "Questo modulo non fa parte del registro attuale.",
    fr: "Ce module ne fait pas partie du registre actuel.",
    de: "Dieses Modul ist nicht Teil des aktuellen Registers.",
    "pt-br": "Este módulo não faz parte do registro atual.",
    ja: "このモジュールは現在のレジストリには含まれていません。",
    hi: "यह मॉड्यूल वर्तमान रजिस्ट्री का हिस्सा नहीं है.",
    ko: "이 모듈은 현재 레지스트리에 포함되어 있지 않습니다.",
    ru: "Этот модуль не входит в текущий реестр.",
    cs: "Tento modul není součástí aktuálního registru.",
    nl: "Deze module is geen onderdeel van het huidige register.",
    ar: "هذه الوحدة ليست جزءًا من السجل الحالي.",
    he: "המודול הזה לא נמצא ברישום הנוכחי.",
    ca: "Aquest mòdul no forma part del registre actual.",
    eu: "Modulu hau ez dago uneko erregistroan.",
    gl: "Este módulo non forma parte do rexistro actual.",
  }, lang),
  privacyNote: pick({
    en: "When this module goes live you will be asked to opt-in. No data is collected until you do.",
    es: "Cuando este módulo esté activo se te pedirá tu consentimiento. No se recopilan datos hasta entonces.",
    zh: "当此模块上线时,系统将请求您的同意。在此之前不会收集任何数据。",
    it: "Quando questo modulo sarà attivo ti verrà chiesto il consenso. Nessun dato viene raccolto fino ad allora.",
    fr: "Quand ce module sera actif on te demandera ton consentement. Aucune donnée n'est collectée avant.",
    de: "Wenn dieses Modul live geht, wirst du um dein Opt-in gebeten. Bis dahin werden keine Daten erhoben.",
    "pt-br": "Quando este módulo entrar no ar você receberá pedido de opt-in. Nenhum dado é coletado até lá.",
    ja: "このモジュールが公開される際にオプトインが求められます。それまでデータは収集されません。",
    hi: "जब यह मॉड्यूल सक्रिय होगा तब आपकी सहमति माँगी जाएगी. तब तक कोई डेटा एकत्र नहीं किया जाता.",
    ko: "이 모듈이 공개되면 옵트인 요청이 전달됩니다. 그 전까지 데이터를 수집하지 않습니다.",
    ru: "Когда модуль будет запущен, у вас попросят согласие. До этого данные не собираются.",
    cs: "Až bude tento modul aktivní, budete požádáni o souhlas. Do té doby nejsou shromažďována žádná data.",
    nl: "Zodra deze module live gaat, vragen we je opt-in. Tot dan worden er geen gegevens verzameld.",
    ar: "عند تفعيل هذه الوحدة سيُطلب منك إعطاء موافقتك. لا يتم جمع أي بيانات حتى ذلك الحين.",
    he: "כאשר המודול הזה יעלה לאוויר תתבקש להסכים. עד אז לא נאסף שום מידע.",
    ca: "Quan aquest mòdul entri en funcionament se't demanarà que hi donis el consentiment. No es recopilen dades fins que ho facis.",
    eu: "Modulu hau martxan jartzen denean, onartzeko eskatuko zaizu. Ez da daturik biltzen hori egin arte.",
    gl: "Cando este módulo se active pedirásete que aceptes. Non se recollen datos ata que o fagas.",
  }, lang),
  // Per-module
  questionnaires: {
    title: "TEAM Questionnaires",
    tagline: pick({
      en: "Create your profile, complete questionnaires, access your dashboard, manage calendar, wishlists, marketplace requests and rewards.",
      es: "Crea tu perfil, completa cuestionarios, accede a tu panel, gestiona calendario, wishlists, solicitudes de marketplace y recompensas.",
      zh: "创建您的个人资料、完成问卷、进入面板、管理日历、愿望清单、市场请求和奖励。",
      it: "Crea il tuo profilo, compila i questionari, accedi al pannello, gestisci calendario, wishlist, richieste marketplace e ricompense.",
      fr: "Crée ton profil, réponds aux questionnaires, accède au tableau de bord, gère calendrier, wishlists, demandes marketplace et récompenses.",
      de: "Profil erstellen, Fragebögen ausfüllen, Dashboard öffnen, Kalender, Wunschlisten, Marktplatz-Anfragen und Belohnungen verwalten.",
      "pt-br": "Crie seu perfil, complete questionários, acesse seu painel, gerencie agenda, listas de desejos, solicitações de marketplace e recompensas.",
      ja: "プロフィール作成、アンケート、ダッシュボード、カレンダー、ウィッシュリスト、マーケットプレイス、リワードを一括管理。",
      hi: "अपनी प्रोफ़ाइल बनाएँ, प्रश्नावली पूरी करें, डैशबोर्ड देखें, कैलेंडर, विशलिस्ट, मार्केटप्लेस अनुरोध और पुरस्कार प्रबंधित करें.",
      ko: "프로필 생성, 설문 완료, 대시보드, 캘린더, 위시리스트, 마켓플레이스 요청, 리워드를 관리하세요.",
      ru: "Создайте профиль, заполняйте анкеты, открывайте панель, управляйте календарём, списками желаний, запросами маркетплейса и наградами.",
      cs: "Vytvořte profil, vyplňte dotazníky, otevřete dashboard, spravujte kalendář, seznamy přání, požadavky marketplace a odměny.",
      nl: "Maak je profiel, vul vragenlijsten in, open je dashboard, beheer kalender, verlanglijsten, marketplace-aanvragen en beloningen.",
      ar: "أنشئ ملفك الشخصي، أكمل الاستبيانات، ادخل إلى لوحتك، إدارة التقويم، قوائم الرغبات، طلبات السوق والمكافآت.",
      he: "צור פרופיל, מלא שאלונים, היכנס ללוח הבקרה, נהל יומן, רשימות משאלות, בקשות שוק ותגמולים.",
      ca: "Crea el teu perfil, completa qüestionaris, accedeix al teu tauler, gestiona calendari, llistes de desitjos, sol·licituds del mercat i recompenses.",
      eu: "Sortu zure profila, bete galdetegiak, sartu zure aginte-panelera, kudeatu egutegia, desio-zerrendak, merkatuaren eskaerak eta sariak.",
      gl: "Crea o teu perfil, completa cuestionarios, accede ao teu panel, xestiona o calendario, listas de desexos, solicitudes do mercado e recompensas.",
    }, lang),
    taglinePoints: pick(QUEST_BULLETS, lang),
    willSolve: pick({
      en: "Live now — the active TEAM app.",
      es: "Activa ahora — la app TEAM activa.",
      zh: "现已上线 —— 当前的 TEAM 应用。",
      it: "Disponibile ora — l'app TEAM attiva.",
      fr: "Disponible — l'app TEAM active.",
      de: "Jetzt verfügbar — die aktive TEAM-App.",
      "pt-br": "No ar agora — o app TEAM ativo.",
      ja: "現在公開中 — アクティブな TEAM アプリです。",
      hi: "अभी सक्रिय — मौजूदा TEAM ऐप.",
      ko: "현재 활성 — 활성 TEAM 앱.",
      ru: "Уже доступно — активное приложение TEAM.",
      cs: "Aktivní teď — aktivní aplikace TEAM.",
      nl: "Nu live — de actieve TEAM-app.",
      ar: "متاحة الآن — تطبيق TEAM النشط.",
      he: "פעיל עכשיו — אפליקציית TEAM הפעילה.",
      ca: "Activa ara — l'aplicació TEAM en directe.",
      eu: "Zuzenean orain — TEAM aplikazio aktiboa.",
      gl: "Xa dispoñible — a aplicación TEAM en directo.",
    }, lang),
  },
  racket_lab: {
    title: "Racket Lab",
    // Feb 2026 — copy refresh aligned with TEAM OS Hub vision.
    tagline: pick({
      en: "Digitize the technical management of rackets, stringing, equipment, service history and player needs — connecting stringers, players, coaches, clubs and tournaments within the TEAM ecosystem.",
      es: "Digitaliza la gestión técnica de raquetas, encordados, material, historial de servicio y necesidades del jugador, conectando el trabajo de stringers, jugadores, entrenadores, clubes y torneos dentro del ecosistema TEAM.",
      zh: "数字化管理球拍、穿线、装备、服务历史和球员需求 —— 在 TEAM 生态系统内连接穿线师、球员、教练、俱乐部和赛事。",
      it: "Digitalizza la gestione tecnica di racchette, incordature, materiale, storico servizi e bisogni del giocatore — collegando stringers, giocatori, allenatori, club e tornei nell'ecosistema TEAM.",
      fr: "Numérise la gestion technique des raquettes, cordages, matériel, historique de service et besoins du joueur — en connectant cordeurs, joueurs, entraîneurs, clubs et tournois dans l'écosystème TEAM.",
      de: "Digitalisiere das technische Management von Schlägern, Bespannungen, Material, Service-Historie und Spielerbedarf – und verbinde Besaiter, Spieler, Trainer, Clubs und Turniere im TEAM-Ökosystem.",
      "pt-br": "Digitalize a gestão técnica de raquetes, cordagens, material, histórico de serviço e necessidades do jogador — conectando stringers, jogadores, treinadores, clubes e torneios dentro do ecossistema TEAM.",
      ja: "ラケット、ストリンギング、用具、サービス履歴、選手のニーズの技術的管理をデジタル化し、ストリンガー、選手、コーチ、クラブ、トーナメントを TEAM エコシステム内でつなげます。",
      hi: "रैकेट, स्ट्रिंगिंग, सामग्री, सेवा इतिहास और खिलाड़ी की आवश्यकताओं के तकनीकी प्रबंधन को डिजिटल बनाएं — स्ट्रिंगर, खिलाड़ी, कोच, क्लब और टूर्नामेंट को TEAM इकोसिस्टम के भीतर जोड़ते हुए।",
      ko: "라켓, 스트링, 장비, 서비스 이력, 선수의 요구사항에 대한 기술적 관리를 디지털화하여 스트링거, 선수, 코치, 클럽, 토너먼트를 TEAM 생태계 안에서 연결합니다.",
      ru: "Цифровизация технического управления ракетками, натяжкой, инвентарём, историей сервиса и потребностями игрока — связывая стрингеров, игроков, тренеров, клубы и турниры внутри экосистемы TEAM.",
      cs: "Digitalizujte technickou správu raket, vyplétání, vybavení, historie servisu a potřeb hráče — propojte stringery, hráče, trenéry, kluby a turnaje uvnitř ekosystému TEAM.",
      nl: "Digitaliseer het technische beheer van rackets, bespanningen, materiaal, servicehistorie en behoeften van spelers — en verbind bespanners, spelers, coaches, clubs en toernooien binnen het TEAM-ecosysteem.",
      ar: "رقمنة الإدارة الفنية للمضارب والتوتير والمعدات وسجل الخدمة واحتياجات اللاعب — مع ربط أعمال الستينجرز واللاعبين والمدربين والأندية والبطولات داخل منظومة TEAM.",
      he: "דיגיטציה של הניהול הטכני של מחבטים, מיתוח, ציוד, היסטוריית שירות וצרכי השחקן — חיבור בין סטרינגרים, שחקנים, מאמנים, מועדונים וטורנירים בתוך מערכת ה-TEAM.",
      ca: "Digitalitza la gestió tècnica de raquetes, encordats, equipament, historial de serveis i necessitats dels jugadors — connectant encordadors, jugadors, entrenadors, clubs i torneigs dins l'ecosistema TEAM.",
      eu: "Digitalizatu raketak, hari-jarpena, ekipamenduak, zerbitzu-historikoa eta jokalariaren beharren kudeaketa teknikoa — TEAM ekosistemaren barruan hari-jartzaileak, jokalariak, entrenatzaileak, klubak eta txapelketak konektatuz.",
      gl: "Dixitaliza a xestión técnica de raquetas, encordado, equipamento, historial de servizos e necesidades dos xogadores — conectando encordadores, xogadores, adestadores, clubs e torneos dentro do ecosistema TEAM.",
    }, lang),
    taglineLead: pick(RACKET_LEAD, lang),
    taglinePoints: pick(RACKET_BULLETS, lang),
    // Feb 2026 — copy refresh: expanded scope (player, racket, string,
    // tension, tournament, service, history, preferences).
    willSolve: pick({
      en: "Replace paper stringing tickets with a digital workflow offering full traceability per player, racket, string, tension, tournament, technical service and historical preferences.",
      es: "Sustituirá los tickets de encordado en papel por un flujo digital con trazabilidad completa por jugador, raqueta, cordaje, tensión, torneo, servicio técnico y preferencias históricas.",
      zh: "用数字化流程取代纸质穿线工单,实现按球员、球拍、线、张力、赛事、技术服务和历史偏好的完整可追溯。",
      it: "Sostituirà i ticket di incordatura cartacei con un flusso digitale e tracciabilità completa per giocatore, racchetta, corda, tensione, torneo, servizio tecnico e preferenze storiche.",
      fr: "Remplacera les tickets papier de cordage par un flux numérique avec traçabilité complète par joueur, raquette, cordage, tension, tournoi, service technique et préférences historiques.",
      de: "Ersetzt papierbasierte Besaitungs-Tickets durch einen digitalen Workflow mit vollständiger Nachverfolgbarkeit nach Spieler, Schläger, Saite, Spannung, Turnier, technischem Service und historischen Präferenzen.",
      "pt-br": "Substituirá os tickets de cordagem em papel por um fluxo digital com rastreabilidade completa por jogador, raquete, cordagem, tensão, torneio, serviço técnico e preferências históricas.",
      ja: "紙のストリンギング伝票を、選手・ラケット・ストリング・テンション・トーナメント・技術サービス・履歴の好みごとに完全にトレース可能なデジタルワークフローに置き換えます。",
      hi: "कागज़ी स्ट्रिंगिंग टिकट को एक डिजिटल वर्कफ़्लो से बदलेगा जो खिलाड़ी, रैकेट, स्ट्रिंग, टेंशन, टूर्नामेंट, तकनीकी सेवा और ऐतिहासिक प्राथमिकताओं के अनुसार पूर्ण ट्रेसबिलिटी प्रदान करता है।",
      ko: "종이 스트링 티켓을, 선수·라켓·스트링·텐션·토너먼트·기술 서비스·과거 선호도 단위로 완전한 추적성을 제공하는 디지털 워크플로로 대체합니다.",
      ru: "Заменит бумажные тикеты натяжки цифровым процессом с полной прослеживаемостью по игроку, ракетке, струне, натяжению, турниру, техсервису и историческим предпочтениям.",
      cs: "Nahradí papírové vyplétací tikety digitálním workflow s úplnou sledovatelností podle hráče, rakety, struny, napětí, turnaje, technického servisu a historických preferencí.",
      nl: "Vervangt papieren bespanningsbonnen door een digitaal proces met volledige traceerbaarheid per speler, racket, snaar, spanning, toernooi, technische service en historische voorkeuren.",
      ar: "سيحل محل تذاكر التوتير الورقية بسير عمل رقمي مع تتبع كامل لكل لاعب، مضرب، وتر، شدّ، بطولة، خدمة فنية وتفضيلات تاريخية.",
      he: "יחליף את תלושי המיתוח על נייר בתהליך דיגיטלי עם מעקב מלא לכל שחקן, מחבט, מיתר, מתח, טורניר, שירות טכני והעדפות היסטוריות.",
      ca: "Substitueix les fitxes d'encordat en paper per un flux de treball digital que ofereix traçabilitat completa per jugador, raqueta, corda, tensió, torneig, servei tècnic i preferències històriques.",
      eu: "Ordeztu paper-txartelak hari-jarpeneko lan-fluxu digital batekin, jokalariko, raketako, hariko, tentsioko, txapelketako, zerbitzu teknikoko eta lehentasun historikoetako trazabilitate osoa eskainiz.",
      gl: "Substitúe os vales de encordado en papel por un fluxo de traballo dixital con trazabilidade completa por xogador, raqueta, corda, tensión, torneo, servizo técnico e preferencias históricas.",
    }, lang),
    // CA-EU-GL-SPRINT-FEB2026 / F1-b — admin/staff CTA on the racket_lab
    // sublanding (migrated out of the hardcoded "Abrir Racket Lab" string
    // in pages/apps/AppComingSoon.jsx).
    openAdminCta: pick({
      en: "Open Racket Lab",
      es: "Abrir Racket Lab",
      zh: "打开 Racket Lab",
      it: "Apri Racket Lab",
      fr: "Ouvrir Racket Lab",
      de: "Racket Lab öffnen",
      "pt-br": "Abrir Racket Lab",
      ja: "Racket Lab を開く",
      hi: "Racket Lab खोलें",
      ko: "Racket Lab 열기",
      ru: "Открыть Racket Lab",
      cs: "Otevřít Racket Lab",
      nl: "Racket Lab openen",
      ar: "فتح Racket Lab",
      he: "פתח Racket Lab",
      ca: "Obrir Racket Lab",
      eu: "Ireki Racket Lab",
      gl: "Abrir o Racket Lab",
    }, lang),
  },
  // Hidden modules — i18n shipped for parity, surfaced only when
  // `visible: true` is flipped in the registry.
  nutrition_lab: {
    title: "Nutrition Lab",
    tagline: pick({
      en: "Future nutrition planning, hydration, supplements, meal logs and education aligned to training cycles.",
      es: "Futura planificación nutricional, hidratación, suplementos, registros de comidas y educación alineada con ciclos de entrenamiento.",
      zh: "未来的营养规划、补水、补剂、餐食记录和与训练周期对齐的教育。",
      it: "Futura pianificazione nutrizionale, idratazione, integratori, diari pasti ed educazione allineati ai cicli di allenamento.",
      fr: "Future planification nutritionnelle, hydratation, compléments, journaux de repas et éducation alignés sur les cycles d'entraînement.",
      de: "Künftige Ernährungsplanung, Hydration, Supplemente, Mahlzeitenprotokolle und Schulung passend zum Trainingszyklus.",
      "pt-br": "Futuro planejamento nutricional, hidratação, suplementos, diário de refeições e educação alinhada aos ciclos de treino.",
      ja: "将来の栄養計画、水分補給、サプリメント、食事ログ、トレーニングサイクルに沿った教育。",
      hi: "भविष्य की पोषण योजना, हाइड्रेशन, सप्लीमेंट, भोजन लॉग और प्रशिक्षण चक्र के अनुरूप शिक्षा.",
      ko: "향후 영양 계획, 수분 보충, 보조제, 식단 기록 및 훈련 주기에 맞춘 교육.",
      ru: "Будущее планирование питания, гидратация, добавки, дневник питания и обучение в соответствии с тренировочными циклами.",
      cs: "Budoucí plánování výživy, hydratace, doplňky, deníky jídla a vzdělávání sladěné s tréninkovými cykly.",
      nl: "Toekomstige voedingsplanning, hydratatie, supplementen, maaltijdlogboeken en educatie afgestemd op trainingscycli.",
      ar: "تخطيط غذائي مستقبلي، الترطيب، المكملات، سجلات الوجبات وتعليم متوافق مع دورات التدريب.",
      he: "תכנון תזונה עתידי, הידרציה, תוספים, יומני ארוחות וחינוך תואם למחזורי האימון.",
      ca: "Futura planificació nutricional, hidratació, suplements, registres de menjars i educació alineada amb els cicles d'entrenament.",
      eu: "Etorkizuneko nutrizio-plangintza, hidratazio, gehigarriak, otordu-erregistroak eta hezkuntza, entrenamendu-zikloetara lerrokatuta.",
      gl: "Futura planificación nutricional, hidratación, suplementos, rexistro de comidas e educación aliñados con ciclos de adestramento.",
    }, lang),
    willSolve: pick({
      en: "Unify dietary tracking with training calendar so coaches and athletes see one truth.",
      es: "Unificar el seguimiento dietético con el calendario de entrenamiento para que coaches y atletas vean una sola verdad.",
      zh: "将饮食跟踪与训练日历统一,让教练和运动员看到同一份真相。",
      it: "Unificare il tracking dietetico con il calendario di allenamento per dare a coach e atleti un'unica verità.",
      fr: "Unifier le suivi alimentaire avec le calendrier d'entraînement pour une seule vérité partagée.",
      de: "Ernährungs-Tracking und Trainingskalender vereinen — eine einzige Wahrheit für Coach und Athlet.",
      "pt-br": "Unificar o acompanhamento dietético com o calendário de treino — uma só verdade para coaches e atletas.",
      ja: "食事記録とトレーニングカレンダーを統合し、コーチと選手が同じ事実を見られるようにします。",
      hi: "आहार ट्रैकिंग को प्रशिक्षण कैलेंडर के साथ एकीकृत करें — कोच और एथलीट के लिए एक ही सच्चाई.",
      ko: "식단 추적과 훈련 캘린더를 통합해 코치와 선수가 같은 데이터를 보게 합니다.",
      ru: "Объединить контроль питания с тренировочным календарём — единый источник правды для тренера и спортсмена.",
      cs: "Sjednotit sledování stravy s tréninkovým kalendářem, aby trenér i sportovec viděli jednu pravdu.",
      nl: "Voedingsregistratie en trainingskalender verenigen — één bron van waarheid voor coach en atleet.",
      ar: "توحيد تتبع التغذية مع تقويم التدريب لتظهر للمدرب واللاعب حقيقة واحدة.",
      he: "איחוד מעקב תזונתי עם לוח האימונים — מקור אמת אחד למאמן ולספורטאי.",
      ca: "Unifica el seguiment dietètic amb el calendari d'entrenament perquè entrenadors i esportistes vegin una única veritat.",
      eu: "Bateratu dieta-jarraipena entrenamendu-egutegiarekin, entrenatzaileek eta kirolariesk egia bakarra ikusteko.",
      gl: "Unifica o seguimento dietético co calendario de adestramento para que adestadores e atletas vexan unha única verdade.",
    }, lang),
  },
  physical_trainer_lab: {
    title: "Physical Trainer Lab",
    tagline: pick({
      en: "Future S&C plans, drills, gym sessions, workload monitoring and recovery — all coordinated with team calendar.",
      es: "Futuros planes de S&C, ejercicios, sesiones de gimnasio, monitoreo de carga y recuperación — coordinados con el calendario del equipo.",
      zh: "未来的体能训练计划、训练动作、健身房训练、负荷监控和恢复 —— 全部与团队日历协同。",
      it: "Futuri piani S&C, esercizi, sessioni in palestra, monitoraggio carico e recupero — coordinati col calendario di squadra.",
      fr: "Futurs plans S&C, exercices, séances de gym, suivi de charge et récupération — coordonnés avec le calendrier de l'équipe.",
      de: "Künftige S&C-Pläne, Drills, Gym-Sessions, Workload-Monitoring und Regeneration — abgestimmt mit dem Team-Kalender.",
      "pt-br": "Futuros planos de S&C, exercícios, sessões de academia, monitoramento de carga e recuperação — coordenados com a agenda do time.",
      ja: "将来の S&C プラン、ドリル、ジムセッション、負荷モニタリング、リカバリーをチームカレンダーと連携。",
      hi: "भविष्य की S&C योजना, ड्रिल, जिम सेशन, लोड मॉनिटरिंग और रिकवरी — टीम कैलेंडर के साथ समन्वित.",
      ko: "향후 S&C 계획, 드릴, 헬스장 세션, 부하 모니터링 및 회복 — 팀 캘린더와 연동.",
      ru: "Будущие планы S&C, упражнения, тренировки в зале, мониторинг нагрузки и восстановление — синхронизировано с командным календарём.",
      cs: "Budoucí S&C plány, cvičení, posilovna, sledování zátěže a regenerace — koordinováno s týmovým kalendářem.",
      nl: "Toekomstige S&C-plannen, drills, gym-sessies, belastingsmonitoring en herstel — afgestemd op de teamkalender.",
      ar: "خطط S&C مستقبلية، تمارين، جلسات صالة، مراقبة الحمل والتعافي — منسقة مع تقويم الفريق.",
      he: "תוכניות S&C עתידיות, תרגילים, אימוני חדר כושר, ניטור עומס והתאוששות — מתואם עם לוח הקבוצה.",
      ca: "Futurs plans de preparació física, exercicis, sessions de gimnàs, monitoratge de càrrega de treball i recuperació — tot coordinat amb el calendari de l'equip.",
      eu: "Etorkizuneko indar- eta egokitze-planak, ariketak, gimnasioko saioak, lan-kargaren jarraipena eta berreskurapena — guztia talde-egutegiarekin koordinatuta.",
      gl: "Futuros plans de preparación física, exercicios, sesións de ximnasio, monitorización de carga e recuperación — todo coordinado co calendario do equipo.",
    }, lang),
    willSolve: pick({
      en: "Centralise weekly training programs and workload tracking with coach + athlete visibility.",
      es: "Centralizar los programas de entrenamiento semanales y el seguimiento de carga con visibilidad para coach y atleta.",
      zh: "集中管理每周训练计划和负荷跟踪,教练与运动员同步可见。",
      it: "Centralizzare i programmi settimanali e il monitoraggio del carico con visibilità coach + atleta.",
      fr: "Centraliser les programmes hebdomadaires et le suivi de charge avec visibilité coach + athlète.",
      de: "Wöchentliche Trainingspläne und Workload-Tracking zentralisieren — sichtbar für Coach + Athlet.",
      "pt-br": "Centralizar programas semanais e o acompanhamento de carga com visibilidade para coach + atleta.",
      ja: "週間プログラムと負荷追跡を一元化し、コーチ＋選手の双方に可視化。",
      hi: "साप्ताहिक प्रशिक्षण कार्यक्रम और लोड ट्रैकिंग को कोच + एथलीट दृश्यता के साथ केंद्रीकृत करें.",
      ko: "주간 훈련 프로그램과 부하 추적을 코치+선수 가시성으로 통합 관리.",
      ru: "Централизовать недельные программы и мониторинг нагрузки с видимостью для тренера и спортсмена.",
      cs: "Centralizovat týdenní tréninkové programy a sledování zátěže s viditelností pro trenéra + sportovce.",
      nl: "Wekelijkse trainingsprogramma's en belastingsregistratie centraal beheren — zichtbaar voor coach + atleet.",
      ar: "مركزة برامج التدريب الأسبوعية وتتبع الحمل مع رؤية للمدرب واللاعب.",
      he: "ריכוז תוכניות אימון שבועיות ומעקב עומס עם נראות למאמן ולספורטאי.",
      ca: "Centralitza programes setmanals d'entrenament i seguiment de càrrega amb visibilitat per a entrenador i esportista.",
      eu: "Zentralizatu asteko entrenamendu-programak eta lan-kargaren jarraipena entrenatzailearen + kirolarien ikusgarritasunarekin.",
      gl: "Centraliza os programas semanais de adestramento e o seguimento de carga con visibilidade para adestrador + atleta.",
    }, lang),
  },
  travel_events_lab: {
    title: "Travel & Events Lab",
    tagline: pick({
      en: "Future travel, hotels, flights, tickets, event wallet, documents and shared trip planning.",
      es: "Futuros viajes, hoteles, vuelos, entradas, event wallet, documentos y planificación compartida de viajes.",
      zh: "未来的旅行、酒店、航班、门票、活动钱包、文件和共享行程规划。",
      it: "Futuri viaggi, hotel, voli, biglietti, event wallet, documenti e pianificazione condivisa.",
      fr: "Futurs voyages, hôtels, vols, billets, event wallet, documents et planification de voyage partagée.",
      de: "Künftige Reisen, Hotels, Flüge, Tickets, Event-Wallet, Dokumente und geteilte Reiseplanung.",
      "pt-br": "Futuras viagens, hotéis, voos, ingressos, event wallet, documentos e planejamento de viagens compartilhado.",
      ja: "将来の旅行、ホテル、フライト、チケット、イベントウォレット、書類、共同旅行プラン。",
      hi: "भविष्य की यात्रा, होटल, उड़ानें, टिकट, इवेंट वॉलेट, दस्तावेज़ और साझा यात्रा योजना.",
      ko: "향후 여행, 호텔, 항공편, 티켓, 이벤트 월렛, 문서, 공유 여행 계획.",
      ru: "Будущие поездки, отели, авиабилеты, билеты на события, event wallet, документы и совместное планирование.",
      cs: "Budoucí cestování, hotely, lety, vstupenky, event wallet, dokumenty a sdílené plánování cest.",
      nl: "Toekomstige reizen, hotels, vluchten, tickets, event wallet, documenten en gedeelde reisplanning.",
      ar: "السفر، الفنادق، الرحلات الجوية، التذاكر، محفظة الفعاليات، المستندات وتخطيط الرحلات المشترك في المستقبل.",
      he: "נסיעות עתידיות, מלונות, טיסות, כרטיסים, ארנק אירועים, מסמכים ותכנון נסיעות משותף.",
      ca: "Futurs viatges, hotels, vols, bitllets, cartera d'esdeveniments, documents i planificació compartida de viatges.",
      eu: "Etorkizuneko bidaiak, hotelak, hegalak, sarrerak, ekitaldi-zorroa, dokumentuak eta bidaia-lankidetza konpartitua.",
      gl: "Futuras viaxes, hoteis, voos, billetes, carteira de eventos, documentos e planificación compartida de viaxes.",
    }, lang),
    willSolve: pick({
      en: "Turn the calendar into a travel operations hub: each booking auto-links to event, budget and documents.",
      es: "Convertir el calendario en un centro operativo de viajes: cada reserva enlaza con evento, presupuesto y documentos.",
      zh: "把日历变成旅行运营中心:每个预订自动关联活动、预算和文件。",
      it: "Trasformare il calendario in un hub operativo di viaggio: ogni prenotazione collegata a evento, budget e documenti.",
      fr: "Transformer le calendrier en hub opérationnel de voyage : chaque réservation est reliée à l'événement, au budget et aux documents.",
      de: "Den Kalender zur Reise-Operations-Zentrale machen: Jede Buchung verknüpft automatisch mit Event, Budget und Dokumenten.",
      "pt-br": "Transformar o calendário em um hub de operações de viagem: cada reserva ligada a evento, orçamento e documentos.",
      ja: "カレンダーを旅行オペレーションのハブへ。各予約はイベント、予算、書類に自動連携。",
      hi: "कैलेंडर को यात्रा संचालन हब बनाएँ: हर बुकिंग ईवेंट, बजट और दस्तावेज़ों से जुड़ी.",
      ko: "캘린더를 여행 운영 허브로: 각 예약이 이벤트, 예산, 문서와 자동 연결.",
      ru: "Превратить календарь в центр управления поездками: каждая бронь автоматически связана с событием, бюджетом и документами.",
      cs: "Proměnit kalendář v cestovní operační centrum: každá rezervace propojená s akcí, rozpočtem a dokumenty.",
      nl: "De kalender omzetten in een reis-operations hub: elke boeking gekoppeld aan event, budget en documenten.",
      ar: "تحويل التقويم إلى مركز عمليات سفر: كل حجز مربوط بالحدث والميزانية والوثائق.",
      he: "להפוך את היומן למרכז תפעול נסיעות: כל הזמנה מקושרת לאירוע, תקציב ומסמכים.",
      ca: "Converteix el calendari en un centre d'operacions de viatges: cada reserva s'enllaça automàticament a l'esdeveniment, pressupost i documents.",
      eu: "Bihurtu egutegia bidaia-eragiketen gune: erreserba bakoitza ekitaldiari, aurrekontuari eta dokumentuei automatikoki lotzen da.",
      gl: "Converte o calendario nun centro de operacións de viaxe: cada reserva vincúlase automaticamente a evento, orzamento e documentos.",
    }, lang),
  },
  health_habits_lab: {
    title: "Health & Habits Lab",
    tagline: pick({
      en: "Future medical, prevention, healthy habits and wellness tracking connected to sport development.",
      es: "Futura medicina, prevención, hábitos saludables y bienestar conectado al desarrollo deportivo.",
      zh: "未来的医疗、预防、健康习惯和与运动发展相连的健康跟踪。",
      it: "Futura medicina, prevenzione, abitudini sane e benessere connessi allo sviluppo sportivo.",
      fr: "Future médecine, prévention, habitudes saines et bien-être connectés au développement sportif.",
      de: "Künftige Medizin, Prävention, gesunde Gewohnheiten und Wellness, verknüpft mit sportlicher Entwicklung.",
      "pt-br": "Futura medicina, prevenção, hábitos saudáveis e bem-estar conectados ao desenvolvimento esportivo.",
      ja: "将来の医療、予防、健康習慣、ウェルネスをスポーツ育成と連携。",
      hi: "भविष्य की चिकित्सा, रोकथाम, स्वस्थ आदतें और खेल विकास से जुड़ा वेलनेस ट्रैकिंग.",
      ko: "향후 의료, 예방, 건강한 습관, 웰니스를 스포츠 발전과 연동.",
      ru: "Будущая медицина, профилактика, здоровые привычки и трекинг wellness в связке со спортивным развитием.",
      cs: "Budoucí medicína, prevence, zdravé návyky a wellness propojené se sportovním rozvojem.",
      nl: "Toekomstige medische zorg, preventie, gezonde gewoonten en welzijn gekoppeld aan sportontwikkeling.",
      ar: "الطب، الوقاية، العادات الصحية والعافية المرتبطة بالتطور الرياضي في المستقبل.",
      he: "רפואה עתידית, מניעה, הרגלים בריאים ורווחה המחוברים להתפתחות הספורטיבית.",
      ca: "Futur seguiment mèdic, prevenció, hàbits saludables i benestar connectat al desenvolupament esportiu.",
      eu: "Etorkizuneko medikal, prebentzioa, ohitura osasuntsuak eta ongizate-jarraipena kirol-garapenarekin konektatuta.",
      gl: "Futura atención médica, prevención, hábitos saudables e seguimento do benestar conectados ao desenvolvemento deportivo.",
    }, lang),
    willSolve: pick({
      en: "Track preventive habits + medical milestones with strict minor protection and guardian consent.",
      es: "Seguir hábitos preventivos + hitos médicos con protección estricta de menores y consentimiento del tutor.",
      zh: "跟踪预防性习惯与医疗里程碑,严格保护未成年人并由监护人同意。",
      it: "Tracciare abitudini preventive + traguardi medici con rigorosa tutela dei minori e consenso del tutore.",
      fr: "Suivre les habitudes préventives et les jalons médicaux avec protection stricte des mineurs et consentement du tuteur.",
      de: "Präventive Gewohnheiten + medizinische Meilensteine erfassen — mit strengem Minderjährigenschutz und Einwilligung der Erziehungsberechtigten.",
      "pt-br": "Acompanhar hábitos preventivos + marcos médicos com proteção estrita de menores e consentimento do responsável.",
      ja: "予防的習慣と医療マイルストーンを、未成年保護と保護者の同意を厳格に保ちながら追跡。",
      hi: "नाबालिगों की कड़ी सुरक्षा और अभिभावक की सहमति के साथ निवारक आदतें और चिकित्सीय मील के पत्थर ट्रैक करें.",
      ko: "미성년자 보호와 보호자 동의를 엄격히 유지하며 예방 습관 + 의료 이정표를 추적.",
      ru: "Отслеживать профилактические привычки и медицинские вехи со строгой защитой несовершеннолетних и согласием опекуна.",
      cs: "Sledovat preventivní návyky a zdravotní milníky s přísnou ochranou nezletilých a souhlasem opatrovníka.",
      nl: "Preventieve gewoonten + medische mijlpalen volgen met strikte minderjarigenbescherming en toestemming van de voogd.",
      ar: "تتبع العادات الوقائية والمحطات الطبية مع حماية صارمة للقاصرين وموافقة الوصي.",
      he: "מעקב אחר הרגלים מונעים ואבני דרך רפואיות עם הגנה קפדנית על קטינים והסכמת אפוטרופוס.",
      ca: "Segueix hàbits preventius i fites mèdiques amb protecció estricta de menors i consentiment del tutor.",
      eu: "Jarraitu prebentzioko ohiturak + mugarri medikalak adingabeen babes zorrotzarekin eta tutorearen baimenarekin.",
      gl: "Rexistra hábitos preventivos + fitos médicos con estrita protección de menores e consentimento dos titores.",
    }, lang),
  },
  donations_sponsors_lab: {
    title: "Donations & Sponsors Lab",
    tagline: pick({
      en: "Future donation, sponsorship, wishlists, sponsored services and campaign support tools.",
      es: "Futuras donaciones, patrocinios, wishlists, servicios patrocinados y herramientas de apoyo a campañas.",
      zh: "未来的捐赠、赞助、愿望清单、赞助服务和活动支持工具。",
      it: "Future donazioni, sponsorizzazioni, wishlist, servizi sponsorizzati e strumenti di supporto alle campagne.",
      fr: "Futurs dons, sponsoring, wishlists, services sponsorisés et outils de support de campagne.",
      de: "Künftige Spenden, Sponsoring, Wunschlisten, gesponserte Services und Kampagnen-Support-Tools.",
      "pt-br": "Futuras doações, patrocínios, listas de desejos, serviços patrocinados e ferramentas de apoio a campanhas.",
      ja: "将来の寄付、スポンサーシップ、ウィッシュリスト、スポンサード サービスとキャンペーン支援ツール。",
      hi: "भविष्य के दान, प्रायोजन, विशलिस्ट, प्रायोजित सेवाएँ और अभियान समर्थन उपकरण.",
      ko: "향후 기부, 스폰서십, 위시리스트, 스폰서드 서비스 및 캠페인 지원 도구.",
      ru: "Будущие пожертвования, спонсорство, списки желаний, спонсируемые услуги и инструменты поддержки кампаний.",
      cs: "Budoucí dary, sponzoring, seznamy přání, sponzorované služby a nástroje pro kampaně.",
      nl: "Toekomstige donaties, sponsoring, verlanglijsten, gesponsorde diensten en campagnehulpmiddelen.",
      ar: "تبرعات، رعاية، قوائم رغبات، خدمات مدعومة وأدوات دعم الحملات في المستقبل.",
      he: "תרומות, חסויות, רשימות משאלות, שירותים ממומנים וכלי תמיכה בקמפיינים עתידיים.",
      ca: "Futures eines de donació, patrocini, llistes de desitjos, serveis patrocinats i suport de campanyes.",
      eu: "Etorkizuneko dohaintza, babesle, desio-zerrendak, babestutako zerbitzuak eta kanpaina-laguntzako tresnak.",
      gl: "Futuras doazóns, patrocinio, listas de desexos, servizos patrocinados e ferramentas de apoio a campañas.",
    }, lang),
    willSolve: pick({
      en: "Connect player wishlists with sponsor campaigns under a private, opt-in, audit-logged matching engine.",
      es: "Conectar las wishlists de los jugadores con campañas de patrocinadores bajo un motor privado, opt-in y auditado.",
      zh: "在隐私、opt-in 且全程审计的匹配引擎下,把球员愿望清单与赞助商活动连接起来。",
      it: "Collegare le wishlist dei giocatori alle campagne sponsor tramite un motore privato, opt-in e auditato.",
      fr: "Connecter les wishlists des joueurs aux campagnes sponsors via un moteur privé, opt-in et audité.",
      de: "Spieler-Wunschlisten mit Sponsor-Kampagnen über eine private, Opt-in-, auditierte Matching-Engine verbinden.",
      "pt-br": "Conectar as wishlists dos jogadores às campanhas de patrocinadores em um motor privado, opt-in e auditado.",
      ja: "プライベートかつオプトイン、監査ログ付きのマッチングエンジンで、選手のウィッシュリストとスポンサーキャンペーンを接続。",
      hi: "खिलाड़ियों की विशलिस्ट को निजी, opt-in, ऑडिट-लॉग्ड मैचिंग इंजन के तहत प्रायोजक अभियानों से जोड़ें.",
      ko: "비공개·옵트인·감사 로그 매칭 엔진으로 선수 위시리스트와 스폰서 캠페인을 연결.",
      ru: "Соединить списки желаний игроков с спонсорскими кампаниями через приватный, opt-in, аудируемый движок подбора.",
      cs: "Propojit seznamy přání hráčů se sponzorskými kampaněmi pomocí soukromého, opt-in a auditovaného matching enginu.",
      nl: "Verlanglijsten van spelers koppelen aan sponsorcampagnes via een privé, opt-in en geauditeerde matchingengine.",
      ar: "ربط قوائم رغبات اللاعبين بحملات الرعاة عبر محرك مطابقة خاص، opt-in ومُدقّق.",
      he: "חיבור רשימות המשאלות של השחקנים לקמפיינים של נותני חסות במנוע התאמה פרטי, opt-in ועם תיעוד ביקורתי.",
      ca: "Connecta llistes de desitjos dels jugadors amb campanyes de patrocinadors sota un motor de concordança privat, per acceptació i amb registre d'auditoria.",
      eu: "Konektatu jokalariaren desio-zerrendak babesle-kanpainekin motor pribatu baten bidez, hautespeneko, auditoria-erregistroarekin.",
      gl: "Conecta listas de desexos de xogadores con campañas de patrocinadores baixo un motor de emparellamento privado, por subscrición e con rexistro de auditoría.",
    }, lang),
  },
});

export function applyAppsI18n(TRANSLATIONS) {
  for (const lc of LANGS) {
    TRANSLATIONS[lc] = { ...(TRANSLATIONS[lc] || {}), apps: buildApps(lc) };
  }
}
